import React from 'react';

interface HowToEQProps {
  theme?: 'light' | 'dark';
}

const HowToEQ: React.FC<HowToEQProps> = ({ theme }) => {
  const accentColor = "#00CCEB";

  const boxStyle = {
    padding: '25px',
    borderRadius: '20px',
    background: theme === 'dark' ? '#1a1a1a' : '#f0f4f8',
    marginBottom: '40px',
    boxShadow: theme === 'dark' ? '0 4px 12px rgba(0,0,0,0.4)' : '0 2px 8px rgba(0,0,0,0.05)'
  };

  const spectrumData = [
    { range: "20–60 Hz", hear: "Sub‑bass rumble", adj: "Boost slightly for depth; cut if muddy" },
    { range: "60–200 Hz", hear: "Bass body, warmth", adj: "Boost for fullness; cut if boomy" },
    { range: "200–1,000 Hz", hear: "Low‑mids, vocal body", adj: "Cut if boxy; boost for presence" },
    { range: "1–4 kHz", hear: "Clarity, attack", adj: "Boost for detail; cut if harsh" },
    { range: "4–10 kHz", hear: "Treble, sparkle", adj: "Boost for brightness; cut if sharp" },
    { range: "10–20 kHz", hear: "Air, shimmer", adj: "Small boosts only" },
  ];

  return (
    <div style={{ padding: "40px 20px", maxWidth: "900px", margin: "0 auto", lineHeight: "1.6" }}>
      {/* Hero Section */}
      <section style={{ textAlign: 'center', marginBottom: '40px' }}>
        <h1 style={{ fontSize: '3rem', marginBottom: '20px' }}>How to <span style={{ color: accentColor }}>EQ</span></h1>
        <p style={{ fontSize: '1.2rem', maxWidth: '750px', margin: '0 auto' }}>
          🎧 Beginner‑Friendly Guide to EQ’ing Your Home Sound System or Headphones - for Android, iOS (or any other EQ platform)
        </p>
      </section>

      <p style={{ fontSize: '1.1rem', marginBottom: '40px', textAlign: 'center' }}>
        Whether you use SpotEQ31 for Android or iOS or any other app (or hardware), a great EQ (equalizer) setup can make your music sound clearer, fuller, and more enjoyable — without needing expensive gear. This guide walks you through the basics in a simple and practical way. And remember, your taste and hearing is always the final judge if something's good or not.
      </p>

      {/* What is EQ */}
      <div style={boxStyle}>
        <h2 style={{ color: accentColor, marginTop: 0 }}>🎵 What EQ Actually Does</h2>
        <p>
          EQ (equalization) adjusts the balance of frequencies in your audio. Think of it as shaping the “tone” of your sound — boosting what you like, reducing what you don’t.
        </p>
        <p style={{ fontStyle: 'italic', opacity: 0.8 }}>
          EQ is one of the most essential tools for shaping sound, even in simple listening setups.
        </p>
      </div>

      {/* Frequency Spectrum */}
      <div style={boxStyle}>
        <h2 style={{ color: accentColor, marginTop: 0 }}>🎚️ The Frequency Spectrum (Super Simple)</h2>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '10px' }}>
            <thead>
              <tr style={{ borderBottom: `2px solid ${accentColor}` }}>
                <th style={{ textAlign: 'left', padding: '10px' }}>Range</th>
                <th style={{ textAlign: 'left', padding: '10px' }}>What You Hear</th>
                <th style={{ textAlign: 'left', padding: '10px' }}>Typical Adjustments</th>
              </tr>
            </thead>
            <tbody>
              {spectrumData.map((item, i) => (
                <tr key={i} style={{ borderBottom: theme === 'dark' ? '1px solid #333' : '1px solid #ddd' }}>
                  <td style={{ padding: '10px', fontWeight: 'bold' }}>{item.range}</td>
                  <td style={{ padding: '10px' }}>{item.hear}</td>
                  <td style={{ padding: '10px' }}>{item.adj}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Step-by-Step */}
      <div style={boxStyle}>
        <h2 style={{ color: accentColor, marginTop: 0 }}>🛠️ Step‑by‑Step: How to EQ</h2>
        <ol style={{ paddingLeft: '20px' }}>
          <li style={{ marginBottom: '15px' }}>
            <strong>Start With a Goal:</strong> Decide if you want more bass, clearer vocals, or less harsh treble. This “intent first” approach is key.
          </li>
          <li style={{ marginBottom: '15px' }}>
            <strong>Begin With Flat Settings:</strong> Reset your EQ to neutral flat (all sliders at 0). This gives you a clean baseline.
          </li>
          <li style={{ marginBottom: '15px' }}>
            <strong>Use Subtractive EQ First:</strong> Cutting problem frequencies usually sounds more natural than boosting.
            <ul style={{ marginTop: '5px' }}>
              <li>If bass is muddy → cut around 120–200 Hz</li>
              <li>If vocals sound boxy → cut around 300–500 Hz</li>
            </ul>
          </li>
          <li style={{ marginBottom: '15px' }}>
            <strong>Make Small Adjustments:</strong> Stick to 1–3 dB changes. Tiny moves make a big difference.
          </li>
          <li style={{ marginBottom: '15px' }}>
            <strong>Adjust One Range at a Time:</strong> Move slowly: Bass first, then Mids, then Treble.
          </li>
          <li style={{ marginBottom: '15px' }}>
            <strong>Use Test Tracks:</strong> Pick songs you know well to instantly notice changes.
          </li>
          <li>
            <strong>Fine‑Tune for Your Setup:</strong> 
            <ul style={{ marginTop: '5px' }}>
              <li>For speakers: Cut low frequencies if the room amplifies bass.</li>
              <li>For headphones: If vocals feel distant, boost 1–3 kHz slightly.</li>
            </ul>
          </li>
        </ol>
      </div>

      {/* Starting Points */}
      <div style={{ ...boxStyle, borderLeft: `5px solid ${accentColor}` }}>
        <h2 style={{ color: accentColor, marginTop: 0 }}>🎧 Example EQ Starting Points</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
          <div>
            <strong>Bass‑Heavy Headphones</strong>
            <p>Cut 100–200 Hz (reduces boominess)<br/>Boost 2–4 kHz slightly (adds clarity)</p>
          </div>
          <div>
            <strong>Bright or Harsh Headphones</strong>
            <p>Cut 6–8 kHz<br/>Boost 100–200 Hz slightly for warmth</p>
          </div>
          <div>
            <strong>Small Rooms (Speakers)</strong>
            <p>Cut 60–120 Hz (reduces room boom)<br/>Boost 2–3 kHz for vocal clarity</p>
          </div>
        </div>
      </div>

      {/* Pro Tip */}
      <div style={{ ...boxStyle, background: theme === 'dark' ? '#1e252b' : '#e3f2fd', border: `1px solid ${accentColor}` }}>
        <h2 style={{ color: accentColor, marginTop: 0 }}>🧪 Pro Tip: Sweep to Find Problems</h2>
        <p>
          1. Boost a narrow band by +6 dB.<br/>
          2. Reset it and raise the next band instead, slowly sweep across the spectrum.<br/>
          3. When something sounds bad, cut that frequency instead.
        </p>
      </div>

      {/* Sources */}
      <footer style={{ fontSize: '0.8rem', opacity: 0.7, borderTop: '1px solid #444', paddingTop: '20px' }}>
        <h4 style={{ marginBottom: '10px' }}>📚 Sources & Further Reading</h4>
        <ul style={{ listStyle: 'none', padding: 0 }}>
          <li><a href="https://macdrum.com/blog/how-to-eq-audio" target="_blank" rel="noreferrer" style={{ color: accentColor }}>macdrum.com — How to EQ Audio</a></li>
          <li><a href="https://audient.com/tutorial/the-beginners-guide-to-eq/" target="_blank" rel="noreferrer" style={{ color: accentColor }}>Audient — What is EQ and How Do I Use It?</a></li>
          <li><a href="https://makethatlouder.com/eq-cheat-sheet-settings-and-frequencies-explained/" target="_blank" rel="noreferrer" style={{ color: accentColor }}>Make That Louder — EQ Cheat Sheet</a></li>
          <li><a href="https://audiosorcerer.com/post/eq-cheat-sheet/" target="_blank" rel="noreferrer" style={{ color: accentColor }}>Audio Sorcerer — EQ Cheat Sheet</a></li>
          <li><a href="https://blacktidemusic.com/software-and-plugins/how-to-use-an-equalizer/" target="_blank" rel="noreferrer" style={{ color: accentColor }}>Black Tide Music — How to Use an Equalizer</a></li>
        </ul>
      </footer>
    </div>
  );
};

export default HowToEQ;
